import ChatbotWidget from "@/src/components/chatbot/chatbot-widget";
import GlobalNewsCard from "@/src/components/news/global-news-cards";
import BackNavigationButton from "@/src/components/ui-kit/back-navigation";
import ScSearchInput from "@/src/components/ui-kit/search-bar";
import ScTabs from "@/src/components/ui-kit/tabs";
import ToolbarCreateButton from "@/src/components/ui-kit/toolbar-create-button";
import WikiArticleDetail from "@/src/components/wiki/wiki-article-detail/wiki-article-detail";
import WikiCardSkeleton from "@/src/components/wiki/wiki-card-skeleton";
import WikiCategoryArticles from "@/src/components/wiki/wiki-category-articles/wiki-category-articles";
import WikiCategoryCard from "@/src/components/wiki/wiki-category-card/wiki-category-card";
import WikiDraft from "@/src/components/wiki/wiki-draft/wiki-draft";
import WikiSaved from "@/src/components/wiki/wiki-saved/wiki-saved";
import {
  useWikiArticles,
  WikiTab,
} from "@/src/hooks/features/wiki/use-wiki-articles";
import { usePermissions } from "@/src/hooks/use-permissions";
import PermissionDenied from "@/src/components/ui-kit/permission-denied";
import { useResponsiveGap } from "@/src/hooks/use-responsive-gap";
import { CHATBOT_ENABLED } from "@/src/constants/feature-flags";
import { ApiCategoryWithCount } from "@/src/models/wiki.models";
import { router } from "expo-router";
import { useTranslation } from "react-i18next";
import { FlatList, Image, Text, View } from "react-native";

export default function WikiArticlesScreen() {
  const { t } = useTranslation();
  const gap = useResponsiveGap();

  const {
    isAdmin,
    searchText,
    setSearchText,
    tabs,
    active,
    handleTabChange,
    selectedCategory,
    filteredCategories,
    handleCategoryPress,
    categoriesLoading,
    categoriesError,
    selectedArticleId,
    selectedArticle,
    articleLoading,
    handleArticlePress,
    handleBack,
    isSmall,
    showSidePanel,
    numColumns,
    itemWidthClass,
    columnWrapperStyle,
    contentContainerStyle,
    showCategoryBack,
    showHeader,
    newsData,
    newsLoading,
    newsError,
    chatbotWidgetId,
    isChatbotConfigLoading,
  } = useWikiArticles();

  const { canCreate, canView } = usePermissions();
  const canCreateWiki = isAdmin && canCreate("wiki");
  const canViewWiki = canView("wiki");

  const renderContentPanel = () => {
    if (selectedArticleId) {
      if (articleLoading || !selectedArticle) {
        return (
          <View className="flex-1 gap-3 pt-2">
            <View className="h-6 w-3/4 rounded-md bg-ash-3" />
            <View className="flex-row items-center gap-2 mt-1">
              <View className="h-7 w-7 rounded-full bg-ash-3" />
              <View className="h-3 w-28 rounded-md bg-ash-3" />
            </View>
            <View className="h-48 w-full rounded-2xl bg-ash-3 mt-2" />
            <View className="gap-2 mt-3">
              <View className="h-3 w-full rounded-md bg-ash-3" />
              <View className="h-3 w-11/12 rounded-md bg-ash-3" />
              <View className="h-3 w-4/5 rounded-md bg-ash-3" />
              <View className="h-3 w-full rounded-md bg-ash-3" />
              <View className="h-3 w-2/3 rounded-md bg-ash-3" />
            </View>
          </View>
        );
      }
      return (
        <WikiArticleDetail article={selectedArticle} onBack={handleBack} />
      );
    }

    if (active === WikiTab.DRAFT) {
      return <WikiDraft searchText={searchText} />;
    }

    if (active === WikiTab.SAVED) {
      return (
        <WikiSaved
          searchText={searchText}
          selectedCategories={[]}
          onArticlePress={handleArticlePress}
        />
      );
    }

    if (selectedCategory) {
      return (
        <WikiCategoryArticles
          categoryId={selectedCategory.id}
          searchText={searchText}
          onArticlePress={handleArticlePress}
        />
      );
    }

    if (categoriesLoading) {
      return (
        <>
          <Text className="text-sm font-bold mb-4 text-onyx">
            {t("HINT_BROWSE_BY_CATEGORY")}
          </Text>
          <View className="flex-row flex-wrap" style={{ gap: 10 }}>
            {Array.from({ length: numColumns * 2 }).map((_, idx) => (
              <View key={idx} className={itemWidthClass}>
                <WikiCardSkeleton />
              </View>
            ))}
          </View>
        </>
      );
    }

    if (categoriesError) {
      return (
        <View className="flex-1 items-center justify-center">
          <Text className="text-ash-1">{t("ERROR_LOADING_ARTICLES")}</Text>
        </View>
      );
    }

    if (filteredCategories.length === 0) {
      return (
        <View className="flex-1 items-center justify-center gap-4">
          <Image
            source={require("@/src/assets/images/empty-state.png")}
            style={{ width: 100, height: 100 }}
            resizeMode="contain"
          />
          <Text className="text-ash-1">{t("LABEL_NO_CATEGORIES")}</Text>
        </View>
      );
    }

    return (
      <>
        <Text className="text-sm font-bold mb-4 text-onyx">
          {t("HINT_BROWSE_BY_CATEGORY")}
        </Text>
        <FlatList
          key={numColumns}
          data={filteredCategories}
          keyExtractor={(item) => item.categoryID}
          numColumns={numColumns}
          columnWrapperStyle={columnWrapperStyle}
          contentContainerStyle={contentContainerStyle}
          renderItem={({ item }: { item: ApiCategoryWithCount }) => (
            <View className={itemWidthClass}>
              <WikiCategoryCard
                id={item.categoryID}
                name={item.name}
                articleCount={item.articleCount}
                onPress={handleCategoryPress}
              />
            </View>
          )}
        />
      </>
    );
  };

  if (!canViewWiki) {
    return (
      <View className={`bg-obsidian flex-1 w-full ${gap}`}>
        <PermissionDenied />
      </View>
    );
  }

  return (
    <View
      className={`bg-obsidian flex-1 w-full ${gap} ${showSidePanel ? "flex-row" : "flex-col"}`}
    >
      <View
        className={`flex-col gap-4 bg-obsidian ${
          showSidePanel ? "w-full md:w-[68%]" : "flex-1"
        }`}
      >
        {showHeader && (
          <View className="md:border md:border-ash-3 md:bg-chalk md:rounded-2xl">
            <View
              className={`flex-row items-center gap-4 py-3 justify-between md:p-4 md:border-b md:border-ash-3 ${isSmall ? "flex-wrap" : "flex-nowrap"}`}
            >
              <View className="flex-row items-center gap-3 flex-1">
                {showCategoryBack && (
                  <BackNavigationButton
                    onPress={handleBack}
                    iconSize={16}
                    iconClassName="text-onyx"
                    labelClassName="text-onyx"
                    containerStyle={{ marginBottom: 0 }}
                  />
                )}
                <View className="flex-1 md:flex-none md:w-[50%]">
                  <ScSearchInput
                    value={searchText}
                    onChangeText={setSearchText}
                    placeholderKey="LABEL_SEARCH_CATEGORY"
                    containerClassName="rounded-full"
                    surface="screen"
                    onCreatePress={
                      canCreateWiki
                        ? () => router.push("/(tabs)/wiki/wiki-creation")
                        : undefined
                    }
                  />
                </View>
              </View>

              {canCreateWiki && (
                <ToolbarCreateButton
                  text={"BTN_CREATE"}
                  onPress={() => router.push("/(tabs)/wiki/wiki-creation")}
                  hideOnMobile
                />
              )}
            </View>

            <View className="mt-1 justify-center pb-2 md:mt-4 md:px-4 md:pb-4">
              <ScTabs tabs={tabs} active={active} onChange={handleTabChange} />
            </View>
          </View>
        )}

        <View className="flex-col flex-1 overflow-hidden md:border md:border-ash-3 md:bg-chalk md:rounded-2xl md:p-4">
          {renderContentPanel()}
        </View>
      </View>

      {showSidePanel && (
        <GlobalNewsCard
          data={newsData}
          isLoading={newsLoading}
          isError={newsError}
        />
      )}

      {CHATBOT_ENABLED && !isChatbotConfigLoading && chatbotWidgetId && (
        <ChatbotWidget widgetId={chatbotWidgetId} />
      )}
    </View>
  );
}
